import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import { calculateDebtPayoff, compareDebtStrategies, calculateRequiredPayment, DEBT_PRESETS } from '../../lib/calculations'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

const DEBT_COLORS = ['#a855f7', '#22d3ee', '#34d399', '#fbbf24', '#f472b6', '#fb923c', '#818cf8', '#2dd4bf']

let nextId = 1

function createDebt(preset = DEBT_PRESETS[0]) {
  return {
    id: nextId++,
    name: `${preset.label}`,
    type: preset.type,
    balance: 5000,
    rate: preset.defaultRate,
    minPayment: preset.defaultMin,
  }
}

export default function DebtPayoff() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [debts, setDebts] = useState([
    { id: nextId++, name: 'Credit Card', type: 'credit_card', balance: 8500, rate: 24.99, minPayment: 250 },
    { id: nextId++, name: 'Student Loan', type: 'student', balance: 22000, rate: 5.5, minPayment: 250 },
    { id: nextId++, name: 'Auto Loan', type: 'auto', balance: 15000, rate: 5.9, minPayment: 350 },
  ])
  const [strategy, setStrategy] = useState('avalanche')
  const [extraMonthly, setExtraMonthly] = useState(200)

  // Lump sum state
  const [lumpAmount, setLumpAmount] = useState(0)
  const [lumpMonth, setLumpMonth] = useState(1)
  const [lumpTarget, setLumpTarget] = useState(-1) // -1 = follow strategy

  // Target payoff state
  const [whatIfMode, setWhatIfMode] = useState('extra') // 'extra' or 'target'
  const [targetMonths, setTargetMonths] = useState(24)

  const lumpSum = lumpAmount > 0 ? { amount: lumpAmount, month: lumpMonth, targetDebtIndex: lumpTarget } : null

  const addDebt = (presetType) => {
    const preset = DEBT_PRESETS.find(p => p.type === presetType) || DEBT_PRESETS[0]
    setDebts(d => [...d, createDebt(preset)])
  }

  const removeDebt = (id) => {
    if (debts.length <= 1) return
    setDebts(d => d.filter(debt => debt.id !== id))
    // Reset lump target if we removed the targeted debt
    if (lumpTarget >= 0) {
      const idx = debts.findIndex(d => d.id === id)
      if (idx === lumpTarget) setLumpTarget(-1)
    }
  }

  const updateDebt = (id, field, value) => {
    setDebts(d => d.map(debt => debt.id === id ? { ...debt, [field]: value } : debt))
  }

  const comparison = useMemo(() =>
    compareDebtStrategies(debts, extraMonthly, lumpSum),
    [debts, extraMonthly, lumpSum?.amount, lumpSum?.month, lumpSum?.targetDebtIndex]
  )

  // Baseline comparison (no extras at all) for "what-if" savings display
  const baseline = useMemo(() =>
    calculateDebtPayoff(debts, strategy, 0, null),
    [debts, strategy]
  )

  // Target payoff calculation
  const targetPayoff = useMemo(() =>
    calculateRequiredPayment(debts, strategy, targetMonths, lumpSum),
    [debts, strategy, targetMonths, lumpSum?.amount, lumpSum?.month, lumpSum?.targetDebtIndex]
  )

  const result = whatIfMode === 'target' && targetPayoff.result
    ? targetPayoff.result
    : (strategy === 'avalanche' ? comparison.avalanche : comparison.snowball)
  const totalDebt = debts.reduce((s, d) => s + d.balance, 0)
  const totalMinPayments = debts.reduce((s, d) => s + d.minPayment, 0)

  // What-if savings
  const interestSaved = baseline.totalInterest - result.totalInterest
  const monthsSaved = baseline.months - result.months

  // Chart data
  const chartData = useMemo(() => {
    if (!result.schedule.length) return []
    const step = Math.max(1, Math.floor(result.schedule.length / 60))
    return result.schedule
      .filter((_, i) => i % step === 0 || i === result.schedule.length - 1)
      .map(row => {
        const point = { month: row.month, label: `Mo ${row.month}`, total: Math.round(row.totalBalance), lumpApplied: row.lumpApplied }
        result.perDebt.forEach((d) => {
          const sortedIdx = result.perDebt.indexOf(d)
          point[d.name] = Math.round(row.balances[sortedIdx] || 0)
        })
        return point
      })
  }, [result])

  // Find the lump sum month marker for the chart
  const lumpChartMonth = lumpSum ? chartData.find(d => d.lumpApplied)?.month : null

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const m = 14
    let y = m

    const heading = (text, size = 16) => {
      doc.setFontSize(size)
      doc.setTextColor(0)
      doc.text(text, m, y)
      y += size === 16 ? 8 : 6
    }
    const subtext = (text) => {
      doc.setFontSize(9)
      doc.setTextColor(100)
      const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - m * 2)
      doc.text(lines, m, y)
      y += lines.length * 4 + 2
    }
    const checkPage = (need = 30) => {
      if (y + need > doc.internal.pageSize.getHeight() - m) {
        doc.addPage()
        y = m
      }
    }

    // ===== Page 1: Title =====
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text('Debt Payoff Report', m, y + 10)
    y += 14
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, m, y)
    y += 6
    doc.text(`Strategy: ${strategy === 'avalanche' ? 'Avalanche (highest rate first)' : 'Snowball (smallest balance first)'}`, m, y)
    y += 4
    subtext(strategy === 'avalanche'
      ? 'Pays highest-interest debts first. Saves the most money overall.'
      : 'Pays smallest balances first. Faster psychological wins to stay motivated.')
    y += 4

    // ===== Result Summary Cards =====
    heading('Results Summary')
    autoTable(doc, {
      body: [
        ['Total Debt', formatCurrency(totalDebt)],
        ['Payoff Time', `${Math.floor(result.months / 12)}y ${result.months % 12}m`],
        ['Total Interest', formatCurrency(result.totalInterest)],
        ['Total Paid', formatCurrency(result.totalPaid)],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== What-If Payments Section =====
    checkPage(50)
    heading('What-If Payments')

    if (whatIfMode === 'extra') {
      subtext('Mode: Extra Payments -- Add extra monthly payments, a one-time lump sum, or both to see how much faster you can be debt-free.')
      const whatIfRows = [
        ['Extra Monthly Payment', formatCurrency(extraMonthly)],
        ['Monthly Minimums', formatCurrency(totalMinPayments)],
      ]
      if (lumpSum) {
        whatIfRows.push(
          ['One-Time Lump Sum', formatCurrency(lumpSum.amount)],
          ['Lump Sum Applied In', `Month ${lumpSum.month}`],
          ['Lump Sum Target', lumpSum.targetDebtIndex >= 0 ? debts[lumpSum.targetDebtIndex]?.name : 'Follow strategy order'],
        )
      }
      autoTable(doc, {
        body: whatIfRows,
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
      })
      y = doc.lastAutoTable.finalY + 6

      // What-if savings
      if (extraMonthly > 0 || lumpAmount > 0) {
        checkPage(30)
        if (baseline.hitMaxMonths) {
          subtext('WARNING: Without extra payments, some debts would never be paid off. Your minimum payments don\'t fully cover the interest on all debts. Extra payments are essential.')
          if (!result.hitMaxMonths) {
            subtext(`With your extra payments: Debt-free in ${Math.floor(result.months / 12)}y ${result.months % 12}m. ${formatCurrency(result.totalInterest)} total interest.`)
          }
        } else if (interestSaved > 0 || monthsSaved > 0) {
          subtext('Your extra payments save you:')
          const savingsRows = []
          if (interestSaved > 0) savingsRows.push(['Interest Saved', formatCurrency(interestSaved)])
          if (monthsSaved > 0) savingsRows.push(['Time Saved', monthsSaved >= 12 ? `${Math.floor(monthsSaved / 12)}y ${monthsSaved % 12}m earlier payoff` : `${monthsSaved} months earlier payoff`])
          savingsRows.push(['Compared To (min only)', `${Math.floor(baseline.months / 12)}y ${baseline.months % 12}m, ${formatCurrency(baseline.totalInterest)} interest`])
          autoTable(doc, {
            body: savingsRows,
            startY: y,
            theme: 'grid',
            styles: { fontSize: 9 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
          })
          y = doc.lastAutoTable.finalY + 6
        }
      }
    } else {
      // Target mode
      subtext('Mode: Target Payoff Date -- Set a target payoff date and we calculate the extra monthly payment needed to be debt-free by then.')
      const targetRows = [
        ['Target Payoff', `${Math.floor(targetMonths / 12)}y ${targetMonths % 12}m`],
      ]
      if (lumpSum) {
        targetRows.push(
          ['One-Time Lump Sum', formatCurrency(lumpSum.amount)],
          ['Lump Sum Applied In', `Month ${lumpSum.month}`],
          ['Lump Sum Target', lumpSum.targetDebtIndex >= 0 ? debts[lumpSum.targetDebtIndex]?.name : 'Follow strategy'],
        )
      }
      if (targetPayoff.feasible) {
        targetRows.push(
          ['Required Extra/mo', formatCurrency(targetPayoff.requiredExtra)],
          ['On Top of Minimums', formatCurrency(totalMinPayments) + '/mo'],
          ['Total Monthly Payment', formatCurrency(targetPayoff.totalPayment)],
          ['Total Interest', formatCurrency(targetPayoff.totalInterest)],
        )
        if (targetPayoff.requiredExtra === 0) {
          targetRows.push(['Note', 'Your minimum payments already pay this off in time!'])
        }
        if (baseline.totalInterest > targetPayoff.totalInterest) {
          targetRows.push(['Interest Saved vs Min Only', formatCurrency(baseline.totalInterest - targetPayoff.totalInterest)])
        }
      } else {
        targetRows.push(['Status', 'Target may not be achievable with current debt configuration. Try a longer timeframe.'])
      }
      autoTable(doc, {
        body: targetRows,
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
      })
      y = doc.lastAutoTable.finalY + 6
    }

    // ===== Your Debts Section =====
    checkPage(40)
    heading('Your Debts')
    const debtInputRows = debts.map(debt => {
      const monthlyInterest = debt.balance * (debt.rate / 100 / 12)
      const minTooLow = debt.minPayment < monthlyInterest && debt.rate > 0
      const row = [
        debt.name,
        formatCurrency(debt.balance),
        `${debt.rate}%`,
        formatCurrency(debt.minPayment),
      ]
      if (minTooLow) {
        row.push(`WARNING: Min doesn't cover interest (${formatCurrencyExact(monthlyInterest)}/mo)`)
      } else {
        row.push('')
      }
      return row
    })
    autoTable(doc, {
      head: [['Debt Name', 'Balance', 'Rate', 'Min Payment', 'Notes']],
      body: debtInputRows,
      startY: y,
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [80, 80, 80] },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Strategy Comparison =====
    checkPage(50)
    heading('Strategy Comparison')
    autoTable(doc, {
      head: [['Metric', 'Avalanche', 'Snowball', 'Difference']],
      body: [
        [
          'Total Interest',
          formatCurrency(comparison.avalanche.totalInterest),
          formatCurrency(comparison.snowball.totalInterest),
          formatCurrency(comparison.interestDifference) + ' saved (avalanche)',
        ],
        [
          'Payoff Time',
          `${Math.floor(comparison.avalanche.months / 12)}y ${comparison.avalanche.months % 12}m`,
          `${Math.floor(comparison.snowball.months / 12)}y ${comparison.snowball.months % 12}m`,
          comparison.monthsDifference > 0
            ? `${comparison.monthsDifference} months faster (avalanche)`
            : comparison.monthsDifference < 0
              ? `${Math.abs(comparison.monthsDifference)} months faster (snowball)`
              : 'Same payoff time',
        ],
        [
          'Total Paid',
          formatCurrency(comparison.avalanche.totalPaid),
          formatCurrency(comparison.snowball.totalPaid),
          '',
        ],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [80, 80, 80] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Payoff Order (Per-Debt Breakdown) =====
    checkPage(40)
    heading('Payoff Order')
    subtext(`Debts listed in the order they will be paid off using the ${strategy} strategy.`)
    autoTable(doc, {
      head: [['#', 'Debt', 'Balance', 'Rate', 'Min Payment', 'Interest Paid', 'Payoff Time']],
      body: result.perDebt.map((d, i) => [
        i + 1,
        d.name,
        formatCurrency(d.balance),
        `${d.rate}%`,
        formatCurrency(d.minPayment),
        formatCurrency(d.totalInterest),
        `${Math.floor(d.monthsToPayoff / 12)}y ${d.monthsToPayoff % 12}m`,
      ]),
      startY: y,
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [80, 80, 80] },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Chart =====
    if (chartData.length > 0) {
      await addChartToPDF(doc, chartRef, { title: 'Debt Payoff Timeline' })
    }

    doc.save(`debt-payoff-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Strategy + Controls */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-body mb-2">Payoff Strategy</h3>
            <div className="flex rounded-xl border border-card overflow-hidden">
              <button
                onClick={() => setStrategy('avalanche')}
                className={`px-4 py-2.5 text-sm font-medium transition ${
                  strategy === 'avalanche' ? 'bg-active text-heading' : 'text-muted hover:text-heading'
                }`}
              >
                Avalanche (least interest)
              </button>
              <button
                onClick={() => setStrategy('snowball')}
                className={`px-4 py-2.5 text-sm font-medium transition ${
                  strategy === 'snowball' ? 'bg-active text-heading' : 'text-muted hover:text-heading'
                }`}
              >
                Snowball (quick wins)
              </button>
            </div>
          </div>
          <div className="self-end">
            <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
              Export PDF{!isPro ? ' (Pro)' : ''}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted">
          {strategy === 'avalanche'
            ? 'Pays highest-interest debts first. Saves the most money overall.'
            : 'Pays smallest balances first. Faster psychological wins to stay motivated.'}
        </p>
      </GlassCard>

      {/* What-If Controls */}
      <GlassCard>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-body">What-If Payments</h3>
          <div className="flex rounded-xl border border-card overflow-hidden">
            <button
              onClick={() => setWhatIfMode('extra')}
              className={`px-3 py-1.5 text-xs font-medium transition ${
                whatIfMode === 'extra' ? 'bg-active text-heading' : 'text-muted hover:text-heading'
              }`}
            >
              Extra Payments
            </button>
            <button
              onClick={() => setWhatIfMode('target')}
              className={`px-3 py-1.5 text-xs font-medium transition ${
                whatIfMode === 'target' ? 'bg-active text-heading' : 'text-muted hover:text-heading'
              }`}
            >
              Target Date
            </button>
          </div>
        </div>

        {whatIfMode === 'extra' ? (
          <>
            <p className="text-xs text-muted mb-4">Add extra monthly payments, a one-time lump sum, or both to see how much faster you can be debt-free.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Extra Monthly */}
              <div className="p-3 rounded-xl bg-subtle">
                <label className="block text-xs font-medium text-muted mb-2">Extra Monthly Payment</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                  <input
                    type="number"
                    value={extraMonthly}
                    onChange={(e) => setExtraMonthly(parseFloat(e.target.value) || 0)}
                    min={0}
                    className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-7 pr-3"
                  />
                </div>
                <p className="text-[10px] text-faint mt-1">Applied every month on top of minimums</p>
              </div>

              {/* Lump Sum Amount */}
              <div className="p-3 rounded-xl bg-subtle">
                <label className="block text-xs font-medium text-muted mb-2">One-Time Lump Sum</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                  <input
                    type="number"
                    value={lumpAmount}
                    onChange={(e) => setLumpAmount(parseFloat(e.target.value) || 0)}
                    min={0}
                    className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-7 pr-3"
                  />
                </div>
                <p className="text-[10px] text-faint mt-1">Tax refund, bonus, inheritance, etc.</p>
              </div>
            </div>

            {/* Lump sum options — only show when lump amount > 0 */}
            {lumpAmount > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-xl border border-card">
                <div>
                  <label className="block text-xs font-medium text-muted mb-2">Apply In Month</label>
                  <input
                    type="number"
                    value={lumpMonth}
                    onChange={(e) => setLumpMonth(Math.max(1, parseInt(e.target.value) || 1))}
                    min={1}
                    className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 px-3"
                  />
                  <p className="text-[10px] text-faint mt-1">Month 1 = next month, Month 6 = in 6 months</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted mb-2">Apply To</label>
                  <select
                    value={lumpTarget}
                    onChange={(e) => setLumpTarget(parseInt(e.target.value))}
                    className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 px-3"
                  >
                    <option value={-1}>Follow strategy order</option>
                    {debts.map((d, i) => (
                      <option key={d.id} value={i}>{d.name} ({formatCurrency(d.balance)})</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-faint mt-1">Target a specific debt or let the strategy decide</p>
                </div>
              </div>
            )}

            {/* What-if savings summary */}
            {(extraMonthly > 0 || lumpAmount > 0) && (
              <div className={`mt-4 p-3 rounded-xl ${baseline.hitMaxMonths ? 'bg-warning-soft border border-warning-soft' : 'bg-positive-soft border border-positive-soft'}`}>
                {baseline.hitMaxMonths ? (
                  <>
                    <p className="text-xs font-semibold text-warn mb-1">Without extra payments, some debts would never be paid off</p>
                    <p className="text-[10px] text-warn opacity-80">
                      Your minimum payments don't fully cover the interest on all debts. Extra payments are essential.
                    </p>
                    {!result.hitMaxMonths && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-positive">With your extra payments:</p>
                        <p className="text-lg font-bold text-positive">
                          Debt-free in {Math.floor(result.months / 12)}y {result.months % 12}m
                        </p>
                        <p className="text-[10px] text-positive opacity-80">{formatCurrency(result.totalInterest)} total interest</p>
                      </div>
                    )}
                  </>
                ) : (interestSaved > 0 || monthsSaved > 0) ? (
                  <>
                    <p className="text-xs font-semibold text-positive mb-1">Your extra payments save you:</p>
                    <div className="flex gap-6">
                      {interestSaved > 0 && (
                        <div>
                          <p className="text-lg font-bold text-positive">{formatCurrency(interestSaved)}</p>
                          <p className="text-[10px] text-positive opacity-80">in interest</p>
                        </div>
                      )}
                      {monthsSaved > 0 && (
                        <div>
                          <p className="text-lg font-bold text-positive">
                            {monthsSaved >= 12 ? `${Math.floor(monthsSaved / 12)}y ${monthsSaved % 12}m` : `${monthsSaved} months`}
                          </p>
                          <p className="text-[10px] text-positive opacity-80">earlier payoff</p>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-positive opacity-70 mt-1">
                      Compared to minimum payments only ({Math.floor(baseline.months / 12)}y {baseline.months % 12}m, {formatCurrency(baseline.totalInterest)} interest)
                    </p>
                  </>
                ) : null}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-muted mb-4">Set a target payoff date and we'll calculate the extra monthly payment needed to be debt-free by then.</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {/* Target Months */}
              <div className="p-3 rounded-xl bg-subtle">
                <label className="block text-xs font-medium text-muted mb-2">Pay Off In</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      value={targetMonths}
                      onChange={(e) => setTargetMonths(Math.max(1, parseInt(e.target.value) || 1))}
                      min={1}
                      max={600}
                      className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 px-3"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">months</span>
                  </div>
                </div>
                <p className="text-[10px] text-faint mt-1">
                  = {Math.floor(targetMonths / 12)}y {targetMonths % 12}m
                </p>
              </div>

              {/* Quick presets */}
              <div className="p-3 rounded-xl bg-subtle sm:col-span-2">
                <label className="block text-xs font-medium text-muted mb-2">Quick Select</label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: '6 mo', months: 6 },
                    { label: '1 year', months: 12 },
                    { label: '18 mo', months: 18 },
                    { label: '2 years', months: 24 },
                    { label: '3 years', months: 36 },
                    { label: '5 years', months: 60 },
                  ].map(preset => (
                    <button
                      key={preset.months}
                      onClick={() => setTargetMonths(preset.months)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        targetMonths === preset.months
                          ? 'bg-primary text-on-primary'
                          : 'bg-hover text-muted hover:text-heading'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Lump sum option in target mode too */}
            <div className="p-3 rounded-xl bg-subtle mb-4">
              <label className="block text-xs font-medium text-muted mb-2">One-Time Lump Sum (optional)</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                  <input
                    type="number"
                    value={lumpAmount}
                    onChange={(e) => setLumpAmount(parseFloat(e.target.value) || 0)}
                    min={0}
                    className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-7 pr-3"
                    placeholder="0"
                  />
                </div>
                {lumpAmount > 0 && (
                  <>
                    <input
                      type="number"
                      value={lumpMonth}
                      onChange={(e) => setLumpMonth(Math.max(1, parseInt(e.target.value) || 1))}
                      min={1}
                      className="bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 px-3"
                      placeholder="Month #"
                    />
                    <select
                      value={lumpTarget}
                      onChange={(e) => setLumpTarget(parseInt(e.target.value))}
                      className="bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 px-3"
                    >
                      <option value={-1}>Follow strategy</option>
                      {debts.map((d, i) => (
                        <option key={d.id} value={i}>{d.name}</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <p className="text-[10px] text-faint mt-1">A lump sum reduces the extra monthly payment needed</p>
            </div>

            {/* Target payoff result */}
            <div className={`p-4 rounded-xl ${targetPayoff.feasible ? 'bg-primary-soft border border-primary-soft' : 'bg-warning-soft border border-warning-soft'}`}>
              {targetPayoff.feasible ? (
                <>
                  <p className="text-xs font-semibold text-heading mb-2">
                    To be debt-free in {Math.floor(targetMonths / 12) > 0 ? `${Math.floor(targetMonths / 12)}y ` : ''}{targetMonths % 12}m:
                  </p>
                  <div className="flex flex-wrap gap-6">
                    <div>
                      <p className="text-2xl font-bold text-primary">{formatCurrency(targetPayoff.requiredExtra)}<span className="text-sm font-medium text-muted">/mo extra</span></p>
                      <p className="text-[10px] text-muted mt-0.5">on top of {formatCurrency(totalMinPayments)}/mo minimums</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-heading">{formatCurrency(targetPayoff.totalPayment)}</p>
                      <p className="text-[10px] text-muted">total monthly payment</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-heading">{formatCurrency(targetPayoff.totalInterest)}</p>
                      <p className="text-[10px] text-muted">total interest</p>
                    </div>
                  </div>
                  {targetPayoff.requiredExtra === 0 && (
                    <p className="text-xs text-positive mt-2">Your minimum payments already pay this off in time!</p>
                  )}
                  {baseline.totalInterest > targetPayoff.totalInterest && (
                    <p className="text-[10px] text-positive mt-2">
                      Saves {formatCurrency(baseline.totalInterest - targetPayoff.totalInterest)} in interest vs minimum payments only
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs font-semibold text-warn">
                  Target may not be achievable with current debt configuration. Try a longer timeframe.
                </p>
              )}
            </div>
          </>
        )}
      </GlassCard>

      {/* Debt List */}
      <GlassCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-body">Your Debts</h3>
          <div className="flex gap-1 flex-wrap">
            {DEBT_PRESETS.map(p => (
              <button
                key={p.type}
                onClick={() => addDebt(p.type)}
                className="px-2 py-1 text-xs text-muted hover:text-heading bg-hover rounded-lg transition"
                title={`Add ${p.label}`}
              >
                {p.icon}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {debts.map((debt, i) => {
            const monthlyInterest = debt.balance * (debt.rate / 100 / 12)
            const minTooLow = debt.minPayment < monthlyInterest && debt.rate > 0
            return (
              <div key={debt.id} className="p-3 rounded-xl bg-subtle">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DEBT_COLORS[i % DEBT_COLORS.length] }} />
                    <input
                      value={debt.name}
                      onChange={(e) => updateDebt(debt.id, 'name', e.target.value)}
                      className="bg-transparent text-sm font-semibold text-heading focus:outline-none border-b border-transparent focus:border-card"
                    />
                  </div>
                  {debts.length > 1 && (
                    <button onClick={() => removeDebt(debt.id)} className="text-faint hover:text-negative transition p-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <CompactInput label="Balance" value={debt.balance} onChange={v => updateDebt(debt.id, 'balance', v)} prefix="$" />
                  <CompactInput label="Rate" value={debt.rate} onChange={v => updateDebt(debt.id, 'rate', v)} suffix="%" step={0.1} />
                  <CompactInput label="Min Payment" value={debt.minPayment} onChange={v => updateDebt(debt.id, 'minPayment', v)} prefix="$" />
                </div>
                {minTooLow && (
                  <p className="text-[10px] text-warn mt-1.5">
                    Min payment ({formatCurrency(debt.minPayment)}) doesn't cover monthly interest ({formatCurrencyExact(monthlyInterest)}). Balance will grow.
                    Calculation uses {formatCurrencyExact(Math.ceil(monthlyInterest) + 1)}/mo minimum instead.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </GlassCard>

      {/* Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ResultCard label="Total Debt" value={formatCurrency(totalDebt)} color={colors.negative} />
        <ResultCard label="Payoff Time" value={`${Math.floor(result.months / 12)}y ${result.months % 12}m`} color={colors.primary} />
        <ResultCard label="Total Interest" value={formatCurrency(result.totalInterest)} color={colors.warning} />
        <ResultCard label="Total Paid" value={formatCurrency(result.totalPaid)} color={colors.secondary} />
      </div>

      {/* Strategy Comparison */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Strategy Comparison</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className={`p-3 rounded-xl border transition cursor-pointer ${strategy === 'avalanche' ? 'border-primary bg-primary-soft' : 'border-card bg-subtle'}`}
            onClick={() => setStrategy('avalanche')}>
            <p className="text-xs font-semibold text-heading mb-1">Avalanche</p>
            <p className="text-lg font-bold text-heading">{formatCurrency(comparison.avalanche.totalInterest)}</p>
            <p className="text-xs text-muted">interest &middot; {Math.floor(comparison.avalanche.months / 12)}y {comparison.avalanche.months % 12}m</p>
          </div>
          <div className={`p-3 rounded-xl border transition cursor-pointer ${strategy === 'snowball' ? 'border-primary bg-primary-soft' : 'border-card bg-subtle'}`}
            onClick={() => setStrategy('snowball')}>
            <p className="text-xs font-semibold text-heading mb-1">Snowball</p>
            <p className="text-lg font-bold text-heading">{formatCurrency(comparison.snowball.totalInterest)}</p>
            <p className="text-xs text-muted">interest &middot; {Math.floor(comparison.snowball.months / 12)}y {comparison.snowball.months % 12}m</p>
          </div>
          <div className="p-3 rounded-xl bg-subtle flex flex-col justify-center">
            <p className="text-xs text-muted">Avalanche saves</p>
            <p className="text-lg font-bold text-positive">{formatCurrency(comparison.interestDifference)}</p>
            <p className="text-xs text-muted">
              {comparison.monthsDifference > 0
                ? `${comparison.monthsDifference} months faster`
                : comparison.monthsDifference < 0
                  ? `${Math.abs(comparison.monthsDifference)} months slower`
                  : 'Same payoff time'}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Per-Debt Breakdown */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Payoff Order</h3>
        <div className="space-y-2">
          {result.perDebt.map((d, i) => (
            <div key={d.id || i} className="flex items-center gap-3 py-2 border-b border-subtle last:border-0">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{ backgroundColor: DEBT_COLORS[d.originalIndex % DEBT_COLORS.length] }}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading truncate">{d.name}</p>
                <p className="text-xs text-muted">{formatCurrency(d.balance)} at {d.rate}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-heading">{Math.floor(d.monthsToPayoff / 12)}y {d.monthsToPayoff % 12}m</p>
                <p className="text-xs text-negative">{formatCurrency(d.totalInterest)} interest</p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Chart */}
      {chartData.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Debt Payoff Timeline</h3>
          <div ref={chartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  interval={Math.floor(chartData.length / 6)}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v) => formatCurrencyExact(v)}
                />
                <Legend />
                {lumpChartMonth && (
                  <ReferenceLine
                    x={`Mo ${lumpChartMonth}`}
                    stroke={colors.warning}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{ value: `Lump sum`, fill: colors.warning, fontSize: 10, position: 'top' }}
                  />
                )}
                {result.perDebt.map((d) => (
                  <Area
                    key={d.name}
                    type="monotone"
                    dataKey={d.name}
                    stackId="debts"
                    stroke={DEBT_COLORS[d.originalIndex % DEBT_COLORS.length]}
                    fill={DEBT_COLORS[d.originalIndex % DEBT_COLORS.length]}
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}
    </div>
  )
}

function CompactInput({ label, value, onChange, prefix, suffix, step }) {
  return (
    <div>
      <label className="block text-xs text-faint mb-0.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-faint">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={0}
          className={`w-full bg-input border border-input rounded-lg py-1.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix ? 'pl-5 pr-2' : suffix ? 'pl-2 pr-6' : 'px-2'
          }`}
        />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
      </div>
    </div>
  )
}

function ResultCard({ label, value, color }) {
  return (
    <GlassCard className="!p-4">
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
    </GlassCard>
  )
}
